import React, { Fragment, useReducer, useEffect } from "react";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory } from "react-router-dom";
import ai from "../../helpers/axios";
import AirportAutocomplete from "../airports/airport-autocomplete";
import RegionAutocomplete from "../airports/region-autocomplete";

const reducer = (state, action) => {
  switch (action.type) {
    case "departureAirportChanged": {
      return {
        ...state,
        route: {
          ...state.route,
          departureAirport: {
            code: action.value.code,
            localCode: action.value.localCode || "",
            airportId: action.value.value,
          },
        },
        selectedDepartureAirport: action.value,
      };
    }
    case "arrivalAirportChanged": {
      return {
        ...state,
        route: {
          ...state.route,
          arrivalAirport: {
            code: action.value.code,
            localCode: action.value.localCode || "",
            airportId: action.value.value,
          },
        },
        selectedArrivalAirport: action.value,
      };
    }
    case "departureRegionChanged": {
      return {
        ...state,
        route: {
          ...state.route,
          departureRegion: action.value.value,
        },
        selectedDepartureRegion: action.value,
      };
    }
    case "arrivalRegionChanged": {
      return {
        ...state,
        route: {
          ...state.route,
          arrivalRegion: action.value.value,
        },
        selectedArrivalRegion: action.value,
      };
    }
    case "aircraftTypeToggled": {
      const updatedTypes = state.route.eligibleAircraftTypes.map((type) => {
        if (type.aircraftTypeId === action.typeId) {
          return { ...type, enabled: !type.enabled };
        }
        return type;
      });
      return {
        ...state,
        route: {
          ...state.route,
          eligibleAircraftTypes: updatedTypes,
        },
      };
    }
    case "aircraftTypesLoaded": {
      // Initialize eligibleAircraftTypes with all loaded types, all disabled by default
      const eligibleTypes = action.payload.types.map((type) => ({
        name: type.name,
        aircraftTypeId: type._id,
        enabled: false,
      }));
      return {
        ...state,
        allAircraftTypes: action.payload.types,
        route: {
          ...state.route,
          eligibleAircraftTypes: eligibleTypes,
        },
      };
    }
    default:
      break;
  }
  return state;
};

const NewClickToFlyContent = () => {
  const history = useHistory();

  const initialState = {
    route: {
      departureAirport: {
        code: "",
        localCode: "",
        airportId: "",
      },
      arrivalAirport: {
        code: "",
        localCode: "",
        airportId: "",
      },
      departureRegion: "",
      arrivalRegion: "",
      eligibleAircraftTypes: [],
    },
    selectedDepartureAirport: null,
    selectedArrivalAirport: null,
    selectedDepartureRegion: null,
    selectedArrivalRegion: null,
    allAircraftTypes: [],
  };

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [state, dispatch] = useReducer(reducer, initialState);

  const loadAircraftTypes = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const { data } = await ai.auth(token).get("/api/aircraftTypes/list", {
      params: {
        page: 1,
        countPerPage: 100,
      },
    });

    dispatch({
      type: "aircraftTypesLoaded",
      payload: {
        types: data.aircraftTypes || [],
      },
    });
  };

  useEffect(() => {
    loadAircraftTypes();
    document.title = "New ClickToFly Route | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let formData = { ...state.route };

    // Clean up empty fields
    if (!formData.departureAirport.airportId) {
      toast.error("Please select a departure airport");
      return;
    }
    if (!formData.arrivalAirport.airportId) {
      toast.error("Please select an arrival airport");
      return;
    }
    if (!formData.departureRegion) {
      toast.error("Please select a departure region");
      return;
    }
    if (!formData.arrivalRegion) {
      toast.error("Please select an arrival region");
      return;
    }

    await ai.auth(token).post(`/api/clickToFly/create`, formData);

    history.push("/click-to-fly/routes");

    setTimeout(() => {
      toast.success("ClickToFly Route Created Successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }, 50);
  };

  const handleDepartureAirportChange = (airport) => {
    if (airport) {
      dispatch({
        type: "departureAirportChanged",
        value: airport,
      });
    }
  };

  const handleArrivalAirportChange = (airport) => {
    if (airport) {
      dispatch({
        type: "arrivalAirportChanged",
        value: airport,
      });
    }
  };

  const handleDepartureRegionChange = (region) => {
    if (region) {
      dispatch({
        type: "departureRegionChanged",
        value: region,
      });
    }
  };

  const handleArrivalRegionChange = (region) => {
    if (region) {
      dispatch({
        type: "arrivalRegionChanged",
        value: region,
      });
    }
  };

  const handleAircraftTypeToggle = (typeId) => {
    dispatch({
      type: "aircraftTypeToggled",
      typeId: typeId,
    });
  };

  return (
    <Fragment>
      <h2 id="newClickToFlyRouteHeading">New ClickToFly Route</h2>

      <form id="newClickToFlyRoute" onSubmit={handleSubmit}>
        <AirportAutocomplete
          selectedAirport={state.selectedDepartureAirport}
          handleAirportChange={handleDepartureAirportChange}
          label="Departure Airport"
        />

        <AirportAutocomplete
          selectedAirport={state.selectedArrivalAirport}
          handleAirportChange={handleArrivalAirportChange}
          label="Arrival Airport"
        />

        <RegionAutocomplete
          selectedRegion={state.selectedDepartureRegion}
          handleRegionChange={handleDepartureRegionChange}
          label="Departure Region"
        />

        <RegionAutocomplete
          selectedRegion={state.selectedArrivalRegion}
          handleRegionChange={handleArrivalRegionChange}
          label="Arrival Region"
        />

        <div className="form-group">
          <label htmlFor="eligibleAircraftTypes">Eligible Aircraft Types</label>
          <div className="aircraft-types-list">
            {state.route.eligibleAircraftTypes && state.route.eligibleAircraftTypes.map((type) => (
              <div key={type.aircraftTypeId} className="aircraft-type-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={type.enabled}
                    onChange={() => handleAircraftTypeToggle(type.aircraftTypeId)}
                  />
                  {" "}{type.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        <input
          type="submit"
          value="Create Route"
          className="evo-blue base-button"
        />
      </form>
    </Fragment>
  );
};

export default NewClickToFlyContent;

